import axios from 'axios'
import i18n from '@/i18n'
import { apiV3Segment, authenticationSegment } from '../../shared/constants'
import { ErrorUtils } from '../../utils/errorUtils'
import { ProfileUtils } from '../../utils/profileUtils'
import { DateUtils } from '@/utils/dateUtils'

const getDefaultState = () => {
  return {
    profile: {
      quota: {},
      used: {},
      userStorage: {
        tokenWeb: false,
        providerQuota: false
      },
      role: ''
    },
    modalShow: false,
    modalEmailShow: false,
    modalImportUserShow: false,
    password: '',
    passwordConfirmation: '',
    profile_loaded: false,
    lang: '',
    passwordPolicy: '',
    currentPassword: '',
    emailAddress: '',
    showExportUserButton: false,
    exportUserToken: '',
    showImportUserButton: false,
    showApiKeyModal: false,
    userApiKey: {
      exists: false,
      expireDate: '',
      key: ''
    },
    showSshKeyModal: false,
    userBastionSshKey: null
  }
}

const state = getDefaultState()

export default {
  state,
  getters: {
    getProfile: state => {
      return state.profile
    },
    getProfileLoaded: state => {
      return state.profile_loaded
    },
    getPassword: state => {
      return state.password
    },
    getPasswordConfirmation: state => {
      return state.passwordConfirmation
    },
    getShowPasswordModal: state => {
      return state.modalShow
    },
    getShowEmailVerificationModal: state => {
      return state.modalEmailShow
    },
    getShowImportUserModal: state => {
      return state.modalImportUserShow
    },
    getExportUserToken: state => {
      return state.exportUserToken
    },
    getLang: state => {
      return state.lang
    },
    getPasswordPolicy: state => {
      return state.passwordPolicy
    },
    getCurrentPassword: state => {
      return state.currentPassword
    },
    getEmailAddress: state => {
      return state.emailAddress
    },
    getShowExportUserButton: state => {
      return state.showExportUserButton
    },
    getShowImportUserButton: state => {
      return state.showImportUserButton
    },
    getShowApiKeyModal: state => {
      return state.showApiKeyModal
    },
    getUserApiKey: state => {
      return state.userApiKey
    },
    getShowSshKeyModal: state => {
      return state.showSshKeyModal
    },
    getUserBastionSshKey: state => {
      return state.userBastionSshKey
    }
  },
  mutations: {
    resetProfileState: (state) => {
      Object.assign(state, getDefaultState())
    },
    resetPasswordState: state => {
      state.password = ''
      state.passwordConfirmation = ''
      state.currentPassword = ''
    },
    resetEmailAddressState: state => {
      state.emailAddress = ''
    },
    setProfile (state, profile) {
      state.profile = profile
      state.profile_loaded = true
    },
    setPassword (state, password) {
      state.password = password
    },
    setPasswordConfirmation (state, passwordConfirmation) {
      state.passwordConfirmation = passwordConfirmation
    },
    setShowPasswordModal: (state, modalShow) => {
      state.modalShow = modalShow
    },
    setShowEmailVerificationModal: (state, modalEmailShow) => {
      state.modalEmailShow = modalEmailShow
    },
    setShowImportUserModal: (state, modalImportUserShow) => {
      state.modalImportUserShow = modalImportUserShow
    },
    setExportUserToken: (state, exportUserToken) => {
      state.exportUserToken = exportUserToken
    },
    setLang (state, lang) {
      state.lang = lang
    },
    setPasswordPolicy (state, passwordPolicy) {
      state.passwordPolicy = passwordPolicy
    },
    setCurrentPassword (state, currentPassword) {
      state.currentPassword = currentPassword
    },
    setEmailAddress (state, emailAddress) {
      state.emailAddress = emailAddress
    },
    update_profile: (state, profile) => {
      state.profile = { ...state.profile, ...profile }
    },
    setShowExportUserButton: (state, showExportUserButton) => {
      state.showExportUserButton = showExportUserButton
    },
    setShowImportUserButton: (state, showImportUserButton) => {
      state.showImportUserButton = showImportUserButton
    },
    setShowApiKeyModal: (state, showApiKeyModal) => {
      state.showApiKeyModal = showApiKeyModal
    },
    setUserApiKey: (state, userApiKey) => {
      state.userApiKey = userApiKey
    },
    resetUserApiKey: (state) => {
      state.userApiKey = {
        exists: false,
        expireDate: '',
        key: ''
      }
    },
    setShowSshKeyModal: (state, showSshKeyModal) => {
      state.showSshKeyModal = showSshKeyModal
    },
    setUserBastionSshKey: (state, userBastionSshKey) => {
      state.userBastionSshKey = userBastionSshKey
    }
  },
  actions: {
    socket_usersData (context, data) {
      const userData = JSON.parse(data)
      const profile = { email: userData.email, emailVerified: userData.email_verified }
      context.commit('update_profile', profile)
    },
    resetProfileState (context) {
      context.commit('resetProfileState')
    },
    fetchProfile (context) {
      Promise.all([
        axios.get(`${apiV3Segment}/item/user/get-details`),
        axios.get(`${apiV3Segment}/item/user/get-quotas`)
      ])
        .then(([detailsResp, quotasResp]) => {
          const merged = { ...detailsResp.data, ...quotasResp.data }
          context.commit('setProfile', ProfileUtils.parseProfile(merged))
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    updatePassword (context, data) {
      return axios.put(`${apiV3Segment}/item/user/set-password`, data).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    updateEmail (context, data) {
      return axios.put(`${apiV3Segment}/item/user/set-email`, data).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    showPasswordModal (context, show) {
      context.commit('setShowPasswordModal', show)
    },
    showEmailVerificationModal (context, show) {
      context.commit('setShowEmailVerificationModal', show)
    },
    showImportUserModal (context, show) {
      context.commit('setShowImportUserModal', show)
    },
    resetPasswordState (context) {
      context.commit('resetPasswordState')
    },
    saveNewLanguage (context) {
      const lang = context.getters.getLang
      if (lang) {
        axios.put(`${apiV3Segment}/item/user/set-lang`, { lang }, { timeout: 25000 }).catch(e => {
          console.error(e)
        })
      }
    },
    fetchPasswordPolicy (context) {
      return axios.get(`${apiV3Segment}/item/user/get-password-policy`)
        .then(response => {
          context.commit('setPasswordPolicy', response.data)
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    resetEmailAddressState (context) {
      context.commit('resetEmailAddressState')
    },
    resetVPN (context, userId) {
      axios.put(`${apiV3Segment}/item/user/reset-vpn`)
        .then(response => {
          ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.reset-vpn'))
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    generateExportUserToken (context) {
      return axios.put(`${apiV3Segment}/item/user-migration/export-user`, {})
        .then(response => {
          context.commit('setExportUserToken', response.data.token)
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    fetchShowExportUserButton (context, providerId) {
      axios.get(`${apiV3Segment}/item/provider/export/${providerId}`)
        .then(response => {
          context.commit('setShowExportUserButton', response.data.enabled)
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    fetchShowImportUserButton (context, providerId) {
      axios.get(`${apiV3Segment}/item/provider/import/${providerId}`)
        .then(response => {
          context.commit('setShowImportUserButton', response.data.enabled)
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    importUser (context, data) {
      return axios.put(`${apiV3Segment}/item/user-migration/import-user`, data)
        .then(response => {
          window.location.pathname = '/migration'
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    goToExportUser () {
      window.location.pathname = '/export-user'
    },
    showApiKeyModal (context, show) {
      context.commit('setShowApiKeyModal', show)
    },
    fetchUserApiKey (context) {
      return axios.get(`${apiV3Segment}/item/user/get-api-key`)
        .then(response => {
          context.commit('setUserApiKey', {
            exists: response.data.exists,
            expireDate: DateUtils.unixFormatAsDate(response.data.expires),
            key: ''
          })
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    generateApiKey (context, maxDate) {
      const exp = Math.floor((maxDate - new Date()) / 1000 / 60)

      return axios.put(`${authenticationSegment}/api-key`, { expiration_minutes: exp })
        .then(response => {
          context.commit('setUserApiKey', {
            exists: true,
            expireDate: '',
            key: response.data.api_key
          })
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    expireApiKey (context) {
      return axios.delete(`${apiV3Segment}/item/user/expire-api-key`)
        .then(response => {
          context.commit('setUserApiKey', {
            exists: false,
            expireDate: '',
            key: ''
          })
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    showSshKeyModal (context, show) {
      context.commit('setShowSshKeyModal', show)
    },
    fetchUserBastionSshKey (context) {
      return axios.get(`${apiV3Segment}/item/user/bastion-ssh-key`)
        .then(response => {
          context.commit('setUserBastionSshKey', response.data.ssh_key || null)
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
        })
    },
    updateUserBastionSshKey (context, sshKey) {
      return axios.put(`${apiV3Segment}/item/user/bastion-ssh-key`, { ssh_key: sshKey })
        .then(() => {
          context.commit('setUserBastionSshKey', sshKey)
          ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.bastion-ssh-key-updated'))
          return true
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
          return false
        })
    },
    deleteUserBastionSshKey (context) {
      return axios.delete(`${apiV3Segment}/item/user/bastion-ssh-key`)
        .then(() => {
          context.commit('setUserBastionSshKey', null)
          ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.bastion-ssh-key-removed'))
          return true
        })
        .catch(e => {
          ErrorUtils.handleErrors(e, this._vm.$snotify)
          return false
        })
    }
  }
}
